import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ENV_CONFIG } from '../config/environment';

const DevelopmentIndicator = ({ isDarkMode }) => {
  const [backendStatus, setBackendStatus] = useState(null);
  const [dbStatus, setDbStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  // Only show in development environment
  const isDevelopment = ENV_CONFIG.IS_DEVELOPMENT;

  useEffect(() => {
    if (!isDevelopment) return;

    const checkBackendStatus = async () => {
      try {
        // Check integrated backend
        const integratedResponse = await fetch(`${ENV_CONFIG.RECOMMENDATION_API_BASE}/health`, {
          method: 'GET',
          timeout: 3000
        });

        if (integratedResponse.ok) {
          const data = await integratedResponse.json();
          setBackendStatus({
            type: 'integrated',
            port: '3001',
            service: data.service || 'integrated_backend',
            version: data.version || '2.0.0',
            status: 'connected'
          });
          setDbStatus({
            status: 'connected',
            connection: 'local',
            host: ENV_CONFIG.BACKEND_HOST
          });

          setIsVisible(true);
          return;
        }
      } catch (error) {
        // Integrated backend not available
      }

      try {
        // Check main FastAPI backend
        const mainResponse = await fetch('http://localhost:3001/health', {
          method: 'GET',
          timeout: 3000
        });

        if (mainResponse.ok) {
          setBackendStatus({
            type: 'main',
            port: '3001',
            service: 'fastapi_backend',
            version: '1.0.0',
            status: 'connected'
          });
          setDbStatus({
            status: 'connected',
            connection: 'local',
            host: 'localhost:3001'
          });
          setIsVisible(true);
          return;
        }
      } catch (error) {
        // Main backend not available
      }

      // No backend available
      setBackendStatus({
        type: 'none',
        port: 'N/A',
        service: 'no_backend',
        status: 'disconnected'
      });
      setDbStatus(null);
      setIsVisible(true);
    };

    checkBackendStatus();

    // Check every 10 seconds
    const interval = setInterval(checkBackendStatus, 10000);

    return () => clearInterval(interval);
  }, [isDevelopment]);

  if (!isDevelopment || !isVisible) return null;

  const getStatusColor = () => {
    if (!backendStatus) return 'bg-gray-500';

    switch (backendStatus.status) {
      case 'connected':
        return backendStatus.type === 'integrated'
          ? 'bg-green-500'
          : 'bg-blue-500';
      case 'disconnected':
        return 'bg-red-500';
      default:
        return 'bg-yellow-500';
    }
  };

  const getStatusText = () => {
    if (!backendStatus) return 'Checking...';

    switch (backendStatus.type) {
      case 'integrated':
        return `🔧 Integrated Backend (${backendStatus.port})`;
      case 'main':
        return `⚡ Main Backend (${backendStatus.port})`;
      case 'none':
        return '❌ No Backend Available';
      default:
        return 'Unknown Backend';
    }
  };

  const getDetailText = () => {
    if (!backendStatus || backendStatus.type === 'none') {
      return 'Backend services not available';
    }

    return `${backendStatus.service} v${backendStatus.version || 'unknown'}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`fixed top-4 right-4 z-[60] ${
        isDarkMode
          ? 'bg-gray-800/90 border border-gray-600/50'
          : 'bg-white/90 border border-gray-300/50'
      } backdrop-blur-sm rounded-lg shadow-lg px-3 py-2 min-w-[200px]`}
    >
      <div className="flex items-center space-x-2">
        <div className={`w-2 h-2 rounded-full ${getStatusColor()} animate-pulse`} />
        <div className="flex-1">
          <div className={`text-xs font-medium ${
            isDarkMode ? 'text-gray-200' : 'text-gray-800'
          }`}>
            {getStatusText()}
          </div>
          <div className={`text-xs ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {getDetailText()}
          </div>
        </div>
      </div>

      {backendStatus && backendStatus.status === 'connected' && (
        <div className={`mt-1 pt-1 border-t ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex justify-between text-xs mb-1">
            <span className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>
              DEV MODE
            </span>
            <span className={`${
              backendStatus.type === 'integrated'
                ? 'text-green-400'
                : 'text-blue-400'
            }`}>
              ● LIVE
            </span>
          </div>
          {dbStatus && (
            <div className="flex justify-between text-xs">
              <span className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>
                DB: {dbStatus.connection === 'ssh_tunnel' ? 'SSH Tunnel' : 'Local'}
              </span>
              <span className={`${
                dbStatus.status === 'connected'
                  ? 'text-green-400'
                  : dbStatus.status === 'checking'
                  ? 'text-yellow-400'
                  : 'text-red-400'
              }`}>
                {dbStatus.status === 'connected' && '● Connected'}
                {dbStatus.status === 'checking' && '● Checking'}
                {dbStatus.status === 'disconnected' && '● Disconnected'}
              </span>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default DevelopmentIndicator;
